'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';
import type { Lesson } from '@/lib/mockData';

interface Props { lesson: Lesson; }

export default function TeacherToolsSidebar({ lesson }: Props) {
  const [favorited, setFavorited] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  const handleDownload = () => {
    // TODO: Backend integration — GET /api/lessons/{lesson.id}/download — returns signed PDF URL
    toast.success('Downloading lesson PDF…', { description: lesson.title });
  };

  const handleAssign = (platform: string) => {
    // TODO: Backend integration — POST /api/lms/assign with { lessonId, platform, classId }
    toast.success(`Opening ${platform}…`, {
      description: 'Your lesson will be pre-loaded in the assignment creator.',
    });
    setAssignModalOpen(false);
  };

  return (
    <>
      <div className="bg-white rounded-2xl border border-stone-200 shadow-card overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary-500 to-primary-700 p-5">
          <p className="text-primary-100 text-xs font-semibold uppercase tracking-wider mb-1">
            Teacher Tools
          </p>
          <p className="text-white font-semibold text-sm leading-snug">
            Everything you need to teach this lesson
          </p>
        </div>

        <div className="p-5 space-y-3">
          {/* Download PDF */}
          <button
            onClick={handleDownload}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold active:scale-95 transition-all duration-150 shadow-sm"
          >
            <Icon name="ArrowDownTrayIcon" size={18} />
            <div className="text-left">
              <div>Download PDF</div>
              <div className="text-xs text-primary-200 font-normal">Full lesson + all handouts</div>
            </div>
          </button>

          {/* Google Slides */}
          <a
            href="#"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-stone-50 hover:bg-stone-100 border border-stone-200 text-stone-700 text-sm font-medium active:scale-95 transition-all duration-150"
          >
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm flex-shrink-0">
              <Icon name="PresentationChartBarIcon" size={16} className="text-yellow-500" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Open Google Slides</div>
              <div className="text-xs text-stone-500">Editable presentation</div>
            </div>
            <Icon name="ArrowTopRightOnSquareIcon" size={14} className="ml-auto text-stone-400" />
          </a>

          {/* Assign to LMS */}
          <button
            onClick={() => setAssignModalOpen(true)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-accent-50 hover:bg-accent-100 border border-accent-200 text-accent-700 text-sm font-semibold active:scale-95 transition-all duration-150"
          >
            <Icon name="AcademicCapIcon" size={18} />
            Assign to LMS
          </button>

          {/* Favorite */}
          <button
            onClick={() => {
              setFavorited(!favorited);
              toast(favorited ? 'Removed from favorites' : 'Saved to favorites');
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium active:scale-95 transition-all duration-150 ${
              favorited
                ? 'bg-red-50 border-red-200 text-red-600' :'bg-stone-50 border-stone-200 text-stone-600 hover:bg-stone-100'
            }`}
          >
            <Icon name="HeartIcon" variant={favorited ? 'solid' : 'outline'} size={18} />
            {favorited ? 'Saved to Favorites' : 'Save to Favorites'}
          </button>
        </div>

        {/* Purchase section */}
        <div className="border-t border-stone-100 p-5">
          <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">
            Get the Full Unit
          </p>
          {lesson.isFree ? (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 rounded-xl border border-emerald-200">
              <Icon name="CheckCircleIcon" size={16} className="text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Free — no purchase needed</span>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm mb-3">
                <span className="text-stone-600">Single lesson</span>
                <span className="font-bold text-stone-800 tabular-nums">${lesson.price}</span>
              </div>
              <a
                href="https://www.teacherspayteachers.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white text-sm font-semibold active:scale-95 transition-all duration-150"
              >
                <Icon name="ShoppingBagIcon" size={16} />
                Buy on TpT — ${lesson.price}
              </a>
              <Link
                href="/subscription"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-stone-200 text-stone-600 text-sm font-medium hover:bg-stone-50 active:scale-95 transition-all duration-150"
              >
                Or subscribe for unlimited access
              </Link>
            </div>
          )}
        </div>

        {/* Standards note */}
        <div className="border-t border-stone-100 px-5 py-4 bg-stone-50">
          <p className="text-xs text-stone-500 leading-relaxed">
            All lessons are culturally responsive, standards-aligned, and reviewed by a panel of experienced educators.
          </p>
        </div>
      </div>

      {/* Assign LMS Modal */}
      {assignModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setAssignModalOpen(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm animate-slide-up p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-stone-800">Assign to LMS</h3>
              <button
                onClick={() => setAssignModalOpen(false)}
                className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100 transition-colors"
                aria-label="Close assign modal"
              >
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>
            <p className="text-sm text-stone-500 mb-5">
              Choose your learning management system to pre-load this lesson as an assignment.
            </p>
            <div className="space-y-2">
              {[
                { name: 'Google Classroom', icon: 'AcademicCapIcon', color: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' },
                { name: 'Canvas', icon: 'ComputerDesktopIcon', color: 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100' },
                { name: 'Schoology', icon: 'BookOpenIcon', color: 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100' },
              ].map((platform) => (
                <button
                  key={`platform-${platform.name}`}
                  onClick={() => handleAssign(platform.name)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-semibold active:scale-95 transition-all duration-150 ${platform.color}`}
                >
                  <Icon name={platform.icon as any} size={18} />
                  {platform.name}
                  <Icon name="ArrowRightIcon" size={14} className="ml-auto opacity-60" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}